<template>
	<LModal v-model:open="isWarningOpen" title="Still there?">
		<p class="idle-message">
			You'll be signed out in <b class="countdown">{{ countdownDisplay }}</b> for security.
		</p>

		<template #footer>
			<LButton variant="secondary" @click="onSignOut">Sign out</LButton>
			<LButton variant="primary" @click="onStaySignedIn">Stay signed in</LButton>
		</template>
	</LModal>
</template>










<script setup>
/**
 * The idle timeout and the max session duration, for every signed-in page.
 *
 * Idle timeout: after a stretch with no activity the person gets a countdown and
 * has to say they're still there. Letting it run out signs them out with the
 * "signed out after a period of inactivity" reason, so the sign-in page can
 * explain what happened rather than leaving them wondering. Off by default (see
 * config.js's `session` block).
 *
 * Deliberate: activity does NOT dismiss the warning once it's up. A stray mouse
 * move must not keep an unattended screen signed in — the person chooses.
 *
 * Max session duration: an absolute cap on session length regardless of
 * activity, off by default (see config.js's `session` block). No warning - it's
 * a hard ceiling, not a "still there?" check.
 *
 * The countdown itself lives in /helpers/useIdleTimeout.js and the max-duration
 * check in /helpers/useMaxSessionDuration.js; this is only their face and the
 * choice of what timing out means.
 */
import { computed } from 'vue';
import { useIdleTimeout } from "/helpers/useIdleTimeout.js";
import { useMaxSessionDuration } from "/helpers/useMaxSessionDuration.js";
import { signOut, SIGN_OUT_REASON } from "/helpers/session.js";
import { LModal, LButton } from '/components2/index.js';
import config from "/config.js";





//-- Data/Reactive State

// A no-op while config.session.idleTimeoutEnabled is false (see config.js).
const { isWarning, countdownDisplay, staySignedIn } = useIdleTimeout({
	enabled: config.session.idleTimeoutEnabled,
	onTimeout: () => signOut(SIGN_OUT_REASON.Idle),
});

// A no-op while config.session.maxDurationEnabled is false (see config.js).
useMaxSessionDuration({
	onTimeout: () => signOut(SIGN_OUT_REASON.MaxDuration),
});





//-- Computed Properties

// LModal takes a two-way `open`; the composable owns the state, so closing the
// modal any other way (Esc, the X, the backdrop) counts as "yes, I'm here".
const isWarningOpen = computed({
	get: () => isWarning.value,
	set: (value) => { if (value == false) staySignedIn(); },
});





//-- Methods
const onStaySignedIn = () =>
{
	staySignedIn();
};


// They chose to leave rather than letting the countdown run out, so this is an
// ordinary sign-out, not an idle timeout.
const onSignOut = () =>
{
	signOut(SIGN_OUT_REASON.SignedOut);
};
</script>










<style scoped>
.idle-message
{
	font-size: var(--text-base);
	line-height: 1.55;
	color: var(--text-2);
	margin: 0;
}

.countdown
{
	font-weight: 700;
	color: var(--text);
	font-variant-numeric: tabular-nums;
	font-feature-settings: 'tnum' 1;
}
</style>
